/**
 * Paging for the chronological (league) match list — see
 * `STAGE_TAB_COMPETITIONS` and `PAGE_SIZE` in `PredictionsScreen.tsx`.
 *
 * Pure and separate from the screen so the arithmetic that decides *which*
 * matches a player can see is testable without rendering 380 `MatchCard`s:
 * a full La Liga season is 38 rounds × 10 matches, and an off-by-one here
 * silently hides a match someone still needed to predict.
 *
 * Deliberately generic over the match shape — this only needs a round's slug
 * and its already-translated name, so it never has to know what a prediction
 * is.
 */

/** The only field the page picker cares about; `PredictionMatch` satisfies this. */
export interface DatedMatch {
  kickoffAt: string | null;
}

export interface MatchPage<T> {
  /** Always ≥ 1, so "page 1 of 1" is a valid (if unused) state. */
  pageCount: number;
  /** `page` clamped into range — see `pageMatches`. */
  safePage: number;
  /** Index of this page's first match in the full list; the basis for "showing X–Y". */
  pageStart: number;
  matches: T[];
}

/**
 * Slices `matches` into the page at `page`.
 *
 * Clamps rather than trusts `page`: it is component state that outlives the
 * data behind it, so a refetch that shortens the list (a postponed match
 * disappearing, or simply switching bolão while a later page is open) would
 * otherwise leave an out-of-range slice rendering an empty page with no
 * control able to bring it back except Previous.
 */
export function pageMatches<T>(
  matches: readonly T[],
  page: number,
  pageSize: number,
): MatchPage<T> {
  const pageCount = Math.max(1, Math.ceil(matches.length / pageSize));
  const safePage = Math.min(Math.max(page, 0), pageCount - 1);
  const pageStart = safePage * pageSize;
  return {
    pageCount,
    safePage,
    pageStart,
    matches: matches.slice(pageStart, pageStart + pageSize),
  };
}

export interface PageSpan {
  page: number;
  /** Kickoff of the page's first match, ISO; null if it has no date yet. */
  from: string | null;
  /** Kickoff of the page's last match, ISO; null if it has no date yet. */
  to: string | null;
}

/**
 * The kickoff range each page covers, so a season can be navigated by *when*
 * rather than by 17 clicks of Next.
 *
 * Dates rather than round numbers, which is not the obvious choice — but real
 * fixture data settles it: in La Liga 2026/27 as published, a Round 6 match
 * kicks off before some Round 4 matches. Labelling a page by "the round it
 * starts at" would therefore print "Round 6" over a page that is mostly
 * rounds 4 and 5, and a picker whose entries jump 3 → 6 → 4 is worse than no
 * picker. A date range is true by construction for a list that is sorted by
 * date, never duplicates, and matches how anyone actually thinks about a
 * nine-month season ("where's December?").
 *
 * Returns raw ISO strings, leaving `Intl` to the caller: this stays pure and
 * locale-free, which is what makes it testable.
 */
export function pageSpans(matches: readonly DatedMatch[], pageSize: number): PageSpan[] {
  const spans: PageSpan[] = [];
  for (let start = 0; start < matches.length; start += pageSize) {
    const slice = matches.slice(start, start + pageSize);
    // Min/max rather than `slice[0]`/`slice.at(-1)`: the caller's order is
    // not this function's to assume, and undated matches sort to the end of
    // a page regardless, so taking the ends positionally would report `null`
    // as a boundary on any page that has one.
    const times = slice
      .map((m) => m.kickoffAt)
      .filter((value): value is string => value !== null)
      .sort();
    spans.push({
      page: start / pageSize,
      from: times[0] ?? null,
      to: times.at(-1) ?? null,
    });
  }
  return spans;
}
